
import React, { useEffect, useMemo, useState } from 'react';
import { Box, Grid, IconButton, Typography, Menu, MenuItem } from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import HomeIcon from '@mui/icons-material/Home';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useRecoilState, useRecoilValueLoadable } from 'recoil';
import { dateFormate, generateFilterKey } from '../../utils/helper.jsx';
import DynamicBreadcrumbs from '../Shared/Breadcrumb/index.tsx';
import CommonTable from '../Shared/Tables/TableV1.tsx';
import Pagination from '../FlowBuilder/Common/Pagination/index.tsx';
import { campaignTableStateAtom } from '../../Services/_states/index.js';
import { fetchCampaignListSelector } from '../../Services/_states/CampaignPlanning/campaignSelector.ts';
import SearchBox from '../FlowBuilder/Common/SearchBox/index.tsx';

const CampaignLeadsList = () => {
  const navigate = useNavigate();
  const [tableState, setTableState] = useRecoilState(campaignTableStateAtom);
  const fetchedData = useRecoilValueLoadable(fetchCampaignListSelector);

  const breadcrumbsData = [
   {
    label: (
      <Link to="/r/management/manageCampaign">
        <IconButton color="primary">
          <HomeIcon />
        </IconButton>
      </Link>
    ),
    href: "/r/management/manageCampaign",
  },
    { label: 'Dashboards' },
  ];

  useEffect(() => {
    if (fetchedData.state === 'hasValue' && !fetchedData.contents.fromCache) {
      const filterKey = generateFilterKey(tableState.filters);
      setTableState((prev) => ({
        ...prev,
        cache: { ...prev.cache, [filterKey]: fetchedData.contents.data },
        totalCountCache: { ...prev.totalCountCache, [filterKey]: fetchedData.contents.totalCount },
      }));
    }
  }, [fetchedData, setTableState]);

  const handleChangePage = (event, newPage) => {
    setTableState((prev) => ({
      ...prev,
      filters: { ...prev.filters, page: newPage },
    }));
  };

  const handleChangeRowsPerPage = (event) => {
    const value = parseInt(event.target.value, 10) || 10;
    setTableState({
      ...tableState,
      filters: { ...tableState.filters, limit: value, page: 0 },
    });
  };

  const columns = useMemo(() => [
    {
      accessorKey: 'index',
      header: 'S.No.',
      enableSorting: false,
      Cell: ({ row }) => {
        const currentPage = tableState.filters.page || 0;
        const limit = tableState.filters.limit || 10;
        return currentPage * limit + row.index + 1;
      },
      size: 60,
    },
    {
      accessorKey: 'accountName',
      header: 'Account Name',
      enableSorting: false,
    },
    {
      accessorKey: 'campaign_name',
      header: 'Campaign Name',
      enableSorting: false,
      Cell: ({ row }) => {
        const campaign = row.original;
        return (
          <Typography
            sx={{
              color: 'blue',
              // textDecoration: 'underline',
              cursor: 'pointer',
            }}
            onClick={() => {
              const proposalData = { campaign };
              localStorage.setItem('leadWebFormData', JSON.stringify(proposalData));
              navigate(`/r/records/${campaign.proposal_id}/form`);
            }}
          >
            {campaign?.name || '-'}
          </Typography>
        );
      },
    },
    {
      accessorKey: 'tentative_start_date',
      header: 'Start Date',
      enableSorting: false,
      Cell: ({ row }) =>
        dateFormate(row.original?.tentative_start_date, 'DD-MM-YYYY'),
    },
    {
      accessorKey: 'tentative_end_date',
      header: 'End Date',
      enableSorting: false,
      Cell: ({ row }) =>
        dateFormate(row.original?.tentative_end_date, 'DD-MM-YYYY'),
    },
    {
      accessorKey: 'action',
      header: 'Action',
      size: '40px',
      enableSorting: false,
      Cell: ({ row }) => {
        const [anchorEl, setAnchorEl] = useState(null);
        const open = Boolean(anchorEl);

        const handleClick = (event) => {
          setAnchorEl(event.currentTarget);
        };

        const handleClose = () => {
          setAnchorEl(null);
        };

        const handleViewClick = () => {
          const proposalData = { campaign: row?.original };
          localStorage.setItem('leadWebFormData', JSON.stringify(proposalData));
          handleClose();
          navigate(`/r/records/${row.original?.proposal_id}/form`);
        };

        const handleMappingClick = () => {
          const proposalData = { campaign: row?.original };
          localStorage.setItem('leadWebFormData', JSON.stringify(proposalData));
          handleClose();
          navigate(`/r/records/${row.original?.proposal_id}/campaign-mapping`);
        };

        return (
          <>
            <IconButton onClick={handleClick}>
              <MoreVertIcon />
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={open}
              onClose={handleClose}
            >
              <MenuItem onClick={handleViewClick}>View</MenuItem>
              <MenuItem onClick={handleMappingClick}>Mapping List</MenuItem>
            </Menu>
          </>
        );
      },
    }
  ], [tableState.filters.page, tableState.filters.limit, navigate]);

  const filterKey = generateFilterKey(tableState.filters);
  const data = tableState.cache[filterKey] || [];
  const totalCount = tableState.totalCountCache[filterKey] || 0;

  return (
    <Box sx={{ backgroundColor: '#f8f9fa', borderRadius: '8px',padding:"5px" }}>
      <DynamicBreadcrumbs items={breadcrumbsData} onBreadcrumbClick={(event, item) => item?.onClick?.()} />

      <Grid container justifyContent="space-between" alignItems="center">
        <Grid item>
          <Typography variant="h4" fontWeight="bold">
            Dashboards
          </Typography>
        </Grid>
      </Grid>
      <Grid container sx={{ alignItems: 'center', pt: 2, pb: 2.5 }} className='search-box-style'>
        <Grid item xs={12} sm={6} md={2}>
          <SearchBox handleSearch={(e) => setTableState((prev) => ({ ...prev, filters: { ...prev.filters, page: 0, search: e } }))} value={tableState?.filters?.search || ''} />
        </Grid>
      </Grid>

      <CommonTable
        columns={columns}
        data={data}
        muiTableContainerProps={{
          sx: {
            height: 'calc(100vh - 320px)',
            width: '100%',
            overflowY: 'auto',
            '&::-webkit-scrollbar': {
              width: '6px',
            },
          },
        }}
        state={{ isLoading: fetchedData.state === 'loading' }}
        renderBottomToolbar={() => (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Pagination
              totalCount={totalCount}
              rowsPerPage={tableState.filters.limit}
              page={tableState.filters.page}
              handleChangePage={handleChangePage}
              handleChangeRowsPerPage={handleChangeRowsPerPage}
            />
          </Box>
        )}
      />
    </Box>
  );
};

export default CampaignLeadsList;
